import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, Check, Phone, MessageCircle } from 'lucide-react';
import BlurFade from '../components/primitives/BlurFade';
import Seo from '../components/Seo';
import NotFoundPage from './NotFoundPage';
import CustomPage, { findPublishedCustomPage, RouteLoadingFallback } from './CustomPage';
import { useLang } from '../LangContext';
import { getCategory } from '../catalog';
import { company } from '../i18n';
import { mediaAlt } from '../lib/mediaSeo';
import { usePrices, formatPrice } from '../hooks/usePrices';

const telHref = (p) => `tel:${(p || '').replace(/\s/g, '')}`;
const waHref = (msg) => `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(msg || '')}`;

/**
 * CategoryPage — generic /[category] page driven by /catalog.js.
 * Renders:
 *   • Breadcrumb + Hero (eyebrow, title, intro, lifestyle image, primary CTA)
 *   • Variants grid (cards with badge, image, price, summary, "details" link)
 *   • Strong final CTA block
 */
export default function CategoryPage() {
  const { lang, overlay, contentReady } = useLang();
  const { category } = useParams();
  const location = useLocation();
  const data = getCategory(lang, category);
  const prices = usePrices();

  if (!contentReady) return <RouteLoadingFallback />;
  if (findPublishedCustomPage(overlay, location.pathname)) return <CustomPage fallback={null} />;
  if (!data) return <CustomPage fallback={<NotFoundPage />} />;

  const phone = lang === 'de' ? company.phoneDE : company.phoneNL;
  const labels = lang === 'de'
    ? { back: 'Startseite', view: 'Details ansehen', primaryCta: data.ctaLabel, askWa: 'Frag uns auf WhatsApp', allCta: 'Alle Produkte ansehen', alsoCheck: 'Schau dir auch an' }
    : { back: 'Home', view: 'Bekijk details', primaryCta: data.ctaLabel, askWa: 'Vraag het via WhatsApp', allCta: 'Bekijk alle producten', alsoCheck: 'Bekijk ook' };

  // Sibling categories for cross-linking
  const siblings = ['ramen', 'deuren', 'lichtschachten'].filter((c) => c !== category);

  return (
    <article data-testid={`category-${category}`} className="relative bg-[#FAF8F2]">
      <Seo
        title={data.name}
        description={data.intro}
        path={`/${category}`}
        image={data.heroImage}
        lang={lang}
      />
      {/* Hero band */}
      <section className="relative overflow-hidden pt-28 sm:pt-36 pb-16 lg:pb-24">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-14 items-center">
          <div className="lg:col-span-7">
            <BlurFade>
              <nav className="text-[12px] text-[#5C5852] mb-6">
                <Link to="/" className="hover:underline">{labels.back}</Link>
                <span className="px-2 opacity-50">/</span>
                <span className="text-[#1F1D1A]">{data.name}</span>
              </nav>
            </BlurFade>
            <BlurFade delay={0.05}>
              <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-5">{data.eyebrow}</p>
            </BlurFade>
            <BlurFade delay={0.08}>
              <h1 className="font-display text-[#1F1D1A] text-[40px] sm:text-[64px] lg:text-[88px] leading-[1.02]">{data.name}</h1>
            </BlurFade>
            <BlurFade delay={0.18}>
              <p className="mt-7 text-[#5C5852] text-[16px] lg:text-[18px] leading-relaxed max-w-xl">{data.intro}</p>
            </BlurFade>
            <BlurFade delay={0.26}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to="/offerte" data-testid={`cat-${category}-cta`} className="pill pill-leaf">
                  {labels.primaryCta} <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={waHref(lang === 'de' ? `Hallo, ich interessiere mich für ${data.name}.` : `Hallo, ik wil graag advies over ${data.name}.`)}
                  target="_blank" rel="noreferrer"
                  className="pill pill-outline"
                >
                  <MessageCircle className="w-4 h-4" /> {labels.askWa}
                </a>
              </div>
            </BlurFade>
          </div>

          <BlurFade delay={0.2} offset={20} className="lg:col-span-5">
            <div className="relative rounded-[24px] overflow-hidden aspect-[4/5] depth-card bg-white">
              <img src={data.heroImage} alt={mediaAlt(overlay, data.heroImage, lang, data.name)} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </BlurFade>
        </div>
      </section>

      {/* Variants grid */}
      <section className="bg-[#F2EEDF] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {data.variants.map((v, i) => {
              const vPrice = formatPrice(prices, category, v.slug, lang);
              return (
                <BlurFade key={v.slug} delay={i * 0.06} offset={14}>
                  <Link
                    to={`/${category}/${v.slug}`}
                    data-testid={`variant-card-${v.slug}`}
                    className="group block h-full rounded-[22px] overflow-hidden bg-white border border-[#E5E0D2] transition-all duration-500 hover:border-[#F26B1A]/40 hover:shadow-[0_30px_60px_-30px_rgba(242,107,26,0.30)] hover:-translate-y-1"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EEDF]">
                      <img
                        src={v.image}
                        alt={mediaAlt(overlay, v.image, lang, v.name)}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent" />
                      {v.badge && (
                        <span className="absolute top-4 left-4 inline-flex items-center px-2.5 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.15em] font-semibold shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]">
                          {v.badge}
                        </span>
                      )}
                      {vPrice && (
                        <span className={`absolute bottom-4 right-4 inline-flex items-baseline gap-2 px-3 py-1.5 rounded-full backdrop-blur-md border text-[12.5px] font-semibold tracking-wide ${vPrice.isSale ? 'bg-[#F26B1A] text-white border-white/10 shadow-[0_4px_14px_-4px_rgba(242,107,26,0.55)]' : 'bg-white/95 text-[#1F1D1A] border-black/5'}`}>
                          {vPrice.isSale && vPrice.saleLabel && (
                            <span className="text-[10px] uppercase tracking-[0.15em] opacity-90 mr-1">{vPrice.saleLabel}</span>
                          )}
                          {vPrice.display}
                          {vPrice.isSale && (
                            <span className="line-through opacity-70 text-[11px] font-normal">{vPrice.base}</span>
                          )}
                        </span>
                      )}
                    </div>
                    <div className="p-6 lg:p-7">
                      <h3 className="font-display text-[#1F1D1A] text-[24px] lg:text-[28px] leading-tight">{v.name}</h3>
                      {v.tagline && (
                        <p className="mt-2 text-[#F26B1A] text-[14px] font-light leading-snug">{v.tagline}</p>
                      )}
                      <p className="mt-4 text-[#5C5852] text-[14px] leading-relaxed">{v.summary}</p>
                      <span className="inline-flex items-center gap-1.5 mt-5 text-[#1F1D1A] text-[13px] font-medium group-hover:gap-2 transition-all">
                        {labels.view} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                </BlurFade>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cross-link to other categories */}
      <section className="bg-[#FAF8F2] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] font-medium mb-6">{labels.alsoCheck}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {siblings.map((slug) => {
              const sib = getCategory(lang, slug);
              if (!sib) return null;
              return (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="group relative block aspect-[16/9] rounded-[22px] overflow-hidden depth-card"
                >
                  <img src={sib.heroImage} alt={mediaAlt(overlay, sib.heroImage, lang, sib.name)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <p className="text-[11px] uppercase tracking-[0.2em] opacity-80">{sib.eyebrow}</p>
                    <h3 className="font-display text-[28px] lg:text-[36px] leading-tight mt-1">{sib.name}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final contact strip */}
      <section className="bg-[#1F1D1A] text-white py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <p className="text-[11.5px] uppercase tracking-[0.22em] text-[#F26B1A] mb-3">{lang === 'de' ? 'Bereit für ein Angebot?' : 'Klaar voor een offerte?'}</p>
            <h2 className="font-display text-[28px] sm:text-[40px] lg:text-[52px] leading-tight max-w-2xl">
              {lang === 'de' ? 'Beratung in 5 Minuten — wir antworten persönlich.' : 'Advies in 5 minuten — wij antwoorden persoonlijk.'}
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/offerte" className="pill pill-leaf"><Check className="w-4 h-4" /> {data.ctaLabel}</Link>
            <a href={telHref(phone)} className="pill pill-outline text-white border-white/30 hover:bg-white/10"><Phone className="w-4 h-4" /> {phone}</a>
          </div>
        </div>
      </section>
    </article>
  );
}
